import { ReportConfigDto, GeneratedReportDto, QueryOptions } from '@thingslog/repositories';
import { AxiosError, AxiosResponse, AxiosInstance } from 'axios';
import { useQuery, useMutation, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { QueryKeys } from '../enums/QueryKeys';

export interface ScheduledReportsQueryClient {
  // GET endpoints
  getGeneratedReportsForCompanyBetweenDates: (
    companyId: number,
    fromDate: string,
    toDate: string
  ) => Promise<GeneratedReportDto[]>;

  getAllReportConfigsForCompany: (companyId: number) => Promise<ReportConfigDto[]>;

  getReportConfigFromReportName: (reportName: string) => Promise<ReportConfigDto>;

  // Mutation endpoints
  createReportConfig: (reportConfig: ReportConfigDto) => Promise<ReportConfigDto>;

  updateReportConfig: (
    reportName: string,
    reportConfig: ReportConfigDto
  ) => Promise<ReportConfigDto>;

  deleteReportConfig: (reportName: string) => Promise<void>;

  // React Query hooks
  useGeneratedReportsForCompanyBetweenDates: (
    companyId: number,
    fromDate: Date,
    toDate: Date,
    options?: QueryOptions<GeneratedReportDto[]>
  ) => UseQueryResult<GeneratedReportDto[], AxiosError>;

  useAllReportConfigsForCompany: (
    companyId: number,
    options?: QueryOptions<ReportConfigDto[]>
  ) => UseQueryResult<ReportConfigDto[], AxiosError>;

  useReportConfigFromReportName: (
    reportName: string,
    options?: QueryOptions<ReportConfigDto>
  ) => UseQueryResult<ReportConfigDto, AxiosError>;

  useCreateReportConfig: () => UseMutationResult<
    ReportConfigDto,
    AxiosError,
    ReportConfigDto,
    unknown
  >;

  useUpdateReportConfig: () => UseMutationResult<
    ReportConfigDto,
    AxiosError,
    { reportName: string; reportConfig: ReportConfigDto },
    unknown
  >;

  useDeleteReportConfig: () => UseMutationResult<void, AxiosError, string, unknown>;
}

export function createScheduledReportsQueryClient(
  axios: AxiosInstance
): ScheduledReportsQueryClient {
  return new ScheduledReportsImplementation(axios);
}

class ScheduledReportsImplementation implements ScheduledReportsQueryClient {
  public constructor(private axios: AxiosInstance) {}

  // GET methods
  public getGeneratedReportsForCompanyBetweenDates = async (
    companyId: number,
    fromDate: string,
    toDate: string
  ): Promise<GeneratedReportDto[]> => {
    return this.axios
      .get(`/api/v2/generated-reports`, {
        params: {
          companyId,
          fromDate,
          toDate,
        },
      })
      .then((response: AxiosResponse<GeneratedReportDto[]>) => {
        return response.data;
      });
  };

  public getAllReportConfigsForCompany = async (companyId: number): Promise<ReportConfigDto[]> => {
    return this.axios
      .get(`/api/v2/scheduled-reports-config`, {
        params: {
          companyId,
        },
      })
      .then((response: AxiosResponse<ReportConfigDto[]>) => {
        return response.data;
      });
  };

  public getReportConfigFromReportName = async (reportName: string): Promise<ReportConfigDto> => {
    return this.axios
      .get(`/api/v2/scheduled-reports/${reportName}`)
      .then((response: AxiosResponse<ReportConfigDto>) => {
        return response.data;
      });
  };

  // Mutation methods
  public createReportConfig = async (reportConfig: ReportConfigDto): Promise<ReportConfigDto> => {
    return this.axios
      .post(`/api/v2/scheduled-reports-config`, reportConfig)
      .then((response: AxiosResponse<ReportConfigDto>) => {
        return response.data;
      });
  };

  public updateReportConfig = async (
    reportName: string,
    reportConfig: ReportConfigDto
  ): Promise<ReportConfigDto> => {
    return this.axios
      .put(`/api/v2/scheduled-reports-config/${reportName}`, reportConfig)
      .then((response: AxiosResponse<ReportConfigDto>) => {
        return response.data;
      });
  };

  public deleteReportConfig = async (reportName: string): Promise<void> => {
    return this.axios.delete(`/api/v2/scheduled-reports-config/${reportName}`).then(() => {
      return;
    });
  };

  // React Query hooks for GET requests
  public useGeneratedReportsForCompanyBetweenDates = (
    companyId: number,
    fromDate: Date,
    toDate: Date,
    options?: QueryOptions<GeneratedReportDto[]>
  ): UseQueryResult<GeneratedReportDto[], AxiosError> => {
    return useQuery<GeneratedReportDto[], AxiosError>(
      [
        QueryKeys.GetGeneratedReportsForCompanyBetweenDates,
        companyId,
        fromDate.toISOString(),
        toDate.toISOString(),
      ],
      () =>
        this.getGeneratedReportsForCompanyBetweenDates(
          companyId,
          fromDate.toISOString(),
          toDate.toISOString()
        ),
      options
    );
  };

  public useAllReportConfigsForCompany = (
    companyId: number,
    options?: QueryOptions<ReportConfigDto[]>
  ): UseQueryResult<ReportConfigDto[], AxiosError> => {
    return useQuery<ReportConfigDto[], AxiosError>(
      [QueryKeys.GetAllReportConfigsForCompany, companyId],
      () => this.getAllReportConfigsForCompany(companyId),
      options
    );
  };

  public useReportConfigFromReportName = (
    reportName: string,
    options?: QueryOptions<ReportConfigDto>
  ): UseQueryResult<ReportConfigDto, AxiosError> => {
    return useQuery<ReportConfigDto, AxiosError>(
      [QueryKeys.GetReportConfigFromReportName, reportName],
      () => this.getReportConfigFromReportName(reportName),
      options
    );
  };

  // React Query hooks for mutations
  public useCreateReportConfig = (): UseMutationResult<
    ReportConfigDto,
    AxiosError,
    ReportConfigDto,
    unknown
  > => {
    return useMutation<ReportConfigDto, AxiosError, ReportConfigDto>(
      (reportConfig: ReportConfigDto) => this.createReportConfig(reportConfig)
    );
  };

  public useUpdateReportConfig = (): UseMutationResult<
    ReportConfigDto,
    AxiosError,
    { reportName: string; reportConfig: ReportConfigDto },
    unknown
  > => {
    return useMutation<
      ReportConfigDto,
      AxiosError,
      { reportName: string; reportConfig: ReportConfigDto }
    >(({ reportName, reportConfig }: { reportName: string; reportConfig: ReportConfigDto }) =>
      this.updateReportConfig(reportName, reportConfig)
    );
  };

  public useDeleteReportConfig = (): UseMutationResult<void, AxiosError, string, unknown> => {
    return useMutation<void, AxiosError, string>((reportName: string) =>
      this.deleteReportConfig(reportName)
    );
  };
}
